import { useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import CheckLabel from "@pallet/components/CheckLabel"
import {
    Box,
    mq,
    Pill,
    Text,
    textStyles,
    theme,
    useSecondaryThemeColor,
} from "@pallet-hq/hegel"
import { VerticallyCenteredBox } from "components/atoms"
import { TierFeaturesSectionCollectivePass } from "__generated__/TierFeaturesSectionCollectivePass"
import { TierFeaturesSectionApplylist } from "__generated__/TierFeaturesSectionApplylist"
import { useScreenSize } from "utils/hooks/useSize"

type PartnershipTierFeaturesSectionProps = {
    partnershipTier: TierFeaturesSectionCollectivePass
    applylist: TierFeaturesSectionApplylist
    showOutline: boolean
}

export const PartnershipTierFeaturesSection = ({
    partnershipTier,
    applylist,
    showOutline,
}: PartnershipTierFeaturesSectionProps) => {
    // @ts-ignore
    const { colors } = useTheme()
    const featuredTagColor = useSecondaryThemeColor()
    const { isMobile } = useScreenSize()
    const { group, partnershipBenefits } = applylist
    return (
        <MainWrapper
            outlined={showOutline && partnershipTier.isHighlightedTier}
            color={colors.primary}
        >
            <TitleText>Usage</TitleText>
            <Row>
                <VerticallyCenteredBox gap={theme.space.xsmall}>
                    <HeaderText>Active Job Posts</HeaderText>
                    <StyledPill
                        featured={partnershipTier.isFeatured}
                        color={colors.primary}
                        textColor={featuredTagColor}
                    >
                        {partnershipTier.isFeatured ? "Featured" : "Standard"}
                    </StyledPill>
                </VerticallyCenteredBox>
                <FeatureText>{partnershipTier.activeJobPostLimit}</FeatureText>
            </Row>
            {group && (
                <Row>
                    <HeaderText>Intro Requests</HeaderText>
                    <FeatureText>
                        {partnershipTier.unlimitedIntroRequests
                            ? "Unlimited"
                            : `${partnershipTier.introRequestLimit}/mo`}
                    </FeatureText>
                </Row>
            )}
            <Row>
                <HeaderText>Seats</HeaderText>
                <FeatureText>Unlimited</FeatureText>
            </Row>
            {!!group?.placementAmount && (
                <Row>
                    <HeaderText>Placement Fee</HeaderText>
                    <FeatureText>
                        {group?.placementIsPercentage
                            ? `${group.placementAmount}% per hire`
                            : `$${group?.placementAmount} per hire`}
                    </FeatureText>
                </Row>
            )}
            <TitleText>Features</TitleText>
            {group && (
                <CheckLabel
                    label="Access to talent database"
                    tooltipText="Partners receive an updated batch of new, open to work candidates from my community. Select which ones you want to talk to and get directly referred"
                />
            )}
            <CheckLabel
                label="Job pinned to top of board"
                enabled={partnershipTier.isFeatured}
            />
            {!!partnershipBenefits.length &&
                partnershipBenefits.map(benefit => {
                    const tierBenefit = partnershipTier.additionalBenefits.find(
                        b => b?.benefit.name === benefit.name
                    )
                    if (isMobile && !tierBenefit) return null
                    return (
                        <CheckLabel
                            key={benefit.id}
                            label={benefit.name}
                            enabled={!!tierBenefit}
                            tooltipText={benefit.description || ""}
                        />
                    )
                })}
        </MainWrapper>
    )
}

const MainWrapper = styled.div<{ outlined: boolean; color: string }>`
    display: flex;
    flex-direction: column;
    padding: ${theme.space.small} ${theme.space.medium} ${theme.space.large};
    background: ${theme.colors.white};
    border-radius: 0 0 8px 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
    box-sizing: border-box;
    width: 100%;
    ${mq({
        maxWidth: ["none", "304px"],
        gridRow: [undefined, 3],
        marginBottom: [theme.space.medium, undefined],
    })};
    gap: ${theme.space.small};
    ${({ outlined, color }) =>
        outlined
            ? `border: 2px solid ${color};`
            : `border: 1px solid ${theme.colors.gray300};`};
    border-top: 1px solid ${theme.colors.gray300};
`

const Row = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`

const TitleText = styled(Text)`
    ${textStyles.uiSmall500};
    color: ${theme.colors.gray600};
`

const HeaderText = styled(Text)`
    ${textStyles.uiTiny500};
`

const FeatureText = styled(Text)`
    ${textStyles.paragraphMedium};
    text-align: right;
`

const StyledPill = styled(Pill)<{
    featured: boolean
    color: string
    textColor: string
}>`
    && {
        ${textStyles.uiTiny500};
        padding: 3px 8px;
        ${({ featured, color, textColor }) => {
            return featured
                ? `
                color: ${textColor};
                background-color: ${color};
                border-color: ${color};
            `
                : `
                color: ${theme.colors.gray600};
                background-color: ${theme.colors.gray300};
                border-color: ${theme.colors.gray300};
            `
        }};
    }
`
